<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NModal,
  NButton,
  NSelect,
  NTag,
  NPopconfirm,
  NProgress,
  useMessage,
} from 'naive-ui';
import {
  Users,
  UserPlus,
  Trash2,
  AlertTriangle,
  ShieldCheck,
  RefreshCw,
} from 'lucide-vue-next';
import { billingApi, membersApi, playersApi } from '@/services/api';
import { useAuth } from '@/composables/useAuth';
import CapacityExceededDialog from './CapacityExceededDialog.vue';
import type {
  ServiceId,
  ServiceSeatAllocation,
  SeatLimitExceededError,
  SeatAllocationMember,
  TenantMember,
  PlayerProfile,
} from '@/types/models';

interface Props {
  show: boolean;
  serviceId: ServiceId;
  serviceName?: string;
  teamId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  serviceId: 'VIDEO_ANALYTICS',
  serviceName: 'Video Analytics & AI Replay',
  teamId: undefined,
});

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  (e: 'updated', allocation: ServiceSeatAllocation): void;
}>();

const { t } = useI18n();
const naiveMsg = useMessage();
const { currentRole } = useAuth();

const allocation = ref<ServiceSeatAllocation>({
  serviceId: props.serviceId,
  serviceName: props.serviceName,
  totalSeats: 15,
  allocatedSeats: 0,
  availableSeats: 15,
  assignedMembers: [],
});

const allMembers = ref<TenantMember[]>([]);
const allPlayers = ref<PlayerProfile[]>([]);
const selectedMemberToAssign = ref<string | null>(null);

const isLoading = ref(false);
const isAssigning = ref(false);
const isUnassigning = ref<Record<string, boolean>>({});
const searchQuery = ref('');

// Capacity Exceeded Dialog state
const showCapacityDialog = ref(false);
const capacityErrorData = ref<SeatLimitExceededError | null>(null);

const loadAllocationData = async () => {
  isLoading.value = true;
  try {
    const [allocData, membersList, playersList] = await Promise.all([
      billingApi.getAllocations(props.serviceId, props.teamId),
      membersApi.getMembers(),
      playersApi.getPlayers(props.teamId),
    ]);

    if (allocData) {
      allocation.value = allocData;
    }
    if (membersList) {
      allMembers.value = membersList;
    }
    if (playersList) {
      allPlayers.value = playersList;
    }
  } catch (err: any) {
    naiveMsg.error(err.message || 'Failed to load seat allocations');
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => [props.show, props.serviceId],
  ([newShow]) => {
    if (newShow) {
      loadAllocationData();
      selectedMemberToAssign.value = null;
    }
  },
  { immediate: true }
);

// Candidates available to assign (not already assigned)
const unassignedCandidates = computed(() => {
  const assignedIds = new Set(allocation.value.assignedMembers.map(m => m.memberId));
  const options: { label: string; value: string }[] = [];

  for (const p of allPlayers.value) {
    if (!assignedIds.has(p.id)) {
      options.push({
        label: `#${p.jerseyNumber} ${p.firstName} ${p.lastName} (${p.position})`,
        value: p.id,
      });
    }
  }

  for (const m of allMembers.value) {
    if (!assignedIds.has(m.id) && !options.some(opt => opt.value === m.id)) {
      options.push({
        label: `${m.fullName} [${m.role}] (${m.email})`,
        value: m.id,
      });
    }
  }

  return options;
});

const filteredAssignedMembers = computed(() => {
  const q = searchQuery.value.toLowerCase().trim();
  if (!q) return allocation.value.assignedMembers;
  return allocation.value.assignedMembers.filter(m =>
    m.fullName.toLowerCase().includes(q) ||
    m.email.toLowerCase().includes(q) ||
    (m.jerseyNumber && String(m.jerseyNumber).includes(q))
  );
});

const usagePercentage = computed(() => {
  if (allocation.value.totalSeats <= 0) return 0;
  return Math.min(100, Math.round((allocation.value.allocatedSeats / allocation.value.totalSeats) * 100));
});

// Assign Seat action
const handleAssignSeat = async () => {
  if (!selectedMemberToAssign.value) return;

  isAssigning.value = true;
  try {
    const res = await billingApi.assignSeats(
      props.serviceId,
      [selectedMemberToAssign.value],
      currentRole.value,
      props.teamId
    );

    allocation.value = res.allocation;
    naiveMsg.success(t('seatAllocation.assignedSuccess'));
    selectedMemberToAssign.value = null;
    emit('updated', res.allocation);
  } catch (err: any) {
    if (err.seatLimitError || err.response?.status === 409) {
      capacityErrorData.value = err.seatLimitError || err.response?.data;
      showCapacityDialog.value = true;
    } else {
      naiveMsg.error(err.message || 'Seat assignment failed');
    }
  } finally {
    isAssigning.value = false;
  }
};

// Unassign Seat action
const handleUnassignSeat = async (member: SeatAllocationMember) => {
  isUnassigning.value[member.memberId] = true;
  try {
    const res = await billingApi.unassignSeats(props.serviceId, [member.memberId]);
    allocation.value = res.allocation;
    naiveMsg.info(t('seatAllocation.unassignedSuccess', { name: member.fullName }));
    emit('updated', res.allocation);
  } catch (err: any) {
    if (err.response?.status === 400 || err.response?.data?.errorCode === 'NO_SERVICE_ASSIGNED') {
      naiveMsg.warning(t('seatAllocation.noServiceAssigned'));
    } else {
      naiveMsg.error(err.message || 'Failed to unassign seat');
    }
  } finally {
    isUnassigning.value[member.memberId] = false;
  }
};
</script>

<template>
  <NModal
    :show="show"
    @update:show="(val) => emit('update:show', val)"
    :mask-closable="true"
    transform-origin="center"
  >
    <div class="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-6 animate-in fade-in zoom-in-95 duration-150">
      <!-- Modal Header -->
      <div class="flex items-start justify-between gap-4 pb-3 border-b border-slate-800">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 rounded-2xl bg-sky-500/15 border border-sky-500/30 flex items-center justify-center text-sky-400">
            <Users class="w-6 h-6" />
          </div>
          <div>
            <div class="flex items-center gap-2">
              <h2 class="text-lg font-extrabold text-slate-100">
                {{ t('seatAllocation.modalTitle') }}
              </h2>
              <NTag size="small" type="info" :bordered="false">
                {{ serviceId.replace(/_/g, ' ') }}
              </NTag>
            </div>
            <p class="text-xs text-slate-400 mt-0.5">
              {{ t('seatAllocation.modalSubtitle', { service: serviceName }) }}
            </p>
          </div>
        </div>

        <NButton size="tiny" secondary @click="loadAllocationData" :loading="isLoading" title="Refresh">
          <template #icon><RefreshCw class="w-3.5 h-3.5" /></template>
        </NButton>
      </div>

      <!-- Capacity Usage Meter -->
      <div class="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
        <div class="flex items-center justify-between text-xs">
          <span class="text-slate-400 font-semibold">{{ t('seatAllocation.utilization') }}</span>
          <div class="flex items-center gap-2">
            <span class="font-mono font-bold text-sky-400">{{ allocation.allocatedSeats }}</span>
            <span class="text-slate-500">/</span>
            <span class="font-mono text-slate-300">{{ t('seatAllocation.seatsContracted', { count: allocation.totalSeats }) }}</span>
            <span :class="['text-[11px] font-bold px-2 py-0.5 rounded ml-2', allocation.availableSeats > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400']">
              {{ t('seatAllocation.available', { count: allocation.availableSeats }) }}
            </span>
          </div>
        </div>

        <NProgress
          type="line"
          :percentage="usagePercentage"
          :status="usagePercentage >= 100 ? 'warning' : 'success'"
          :show-indicator="false"
        />
      </div>

      <!-- Assign Seat Form Bar -->
      <div class="p-4 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-2">
        <label class="block text-xs font-bold text-slate-300 uppercase tracking-wider">
          {{ t('seatAllocation.assignSectionTitle') }}
        </label>
        <div class="flex flex-col sm:flex-row gap-2.5">
          <div class="flex-1">
            <NSelect
              v-model:value="selectedMemberToAssign"
              :options="unassignedCandidates"
              :placeholder="t('seatAllocation.selectPlaceholder')"
              size="medium"
              filterable
              clearable
            />
          </div>
          <NButton
            type="primary"
            :disabled="!selectedMemberToAssign || allocation.availableSeats <= 0"
            :loading="isAssigning"
            @click="handleAssignSeat"
            class="font-bold text-xs shrink-0 shadow-md"
          >
            <template #icon><UserPlus class="w-4 h-4" /></template>
            {{ t('seatAllocation.assignBtn') }}
          </NButton>
        </div>
        <p v-if="allocation.availableSeats <= 0" class="text-[11px] text-amber-400 flex items-center gap-1.5 pt-1">
          <AlertTriangle class="w-3.5 h-3.5 shrink-0" />
          {{ t('seatAllocation.capacityFullWarning', { total: allocation.totalSeats }) }}
        </p>
      </div>

      <!-- Assigned Members List -->
      <div class="space-y-3">
        <div class="flex items-center justify-between">
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-300">
            {{ t('seatAllocation.activeMembersTitle', { count: allocation.assignedMembers.length }) }}
          </h3>
          <div class="w-48">
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="t('seatAllocation.searchPlaceholder')"
              class="w-full bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-1 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500"
            />
          </div>
        </div>

        <div class="border border-slate-800 rounded-2xl overflow-hidden bg-slate-950/80">
          <table class="w-full text-xs text-left border-collapse">
            <thead>
              <tr class="border-b border-slate-800/80 bg-slate-950 text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                <th class="py-2.5 px-3">{{ t('seatAllocation.colMember') }}</th>
                <th class="py-2.5 px-3">{{ t('seatAllocation.colRoleJersey') }}</th>
                <th class="py-2.5 px-3">{{ t('seatAllocation.colAssignedDate') }}</th>
                <th class="py-2.5 px-3 text-right">{{ t('seatAllocation.colAction') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-800/60">
              <tr v-if="filteredAssignedMembers.length === 0">
                <td colspan="4" class="py-8 text-center text-slate-500 italic">
                  {{ t('seatAllocation.noAssigned') }}
                </td>
              </tr>
              <tr
                v-for="mem in filteredAssignedMembers"
                :key="mem.memberId"
                class="hover:bg-slate-900/60 transition"
              >
                <td class="py-2.5 px-3">
                  <div class="font-bold text-slate-200">{{ mem.fullName }}</div>
                  <div class="text-[10px] text-slate-400 font-mono">{{ mem.email }}</div>
                </td>
                <td class="py-2.5 px-3">
                  <div class="flex items-center gap-1.5">
                    <NTag size="tiny" :bordered="false" type="info">
                      {{ mem.role || 'MEMBER' }}
                    </NTag>
                    <span v-if="mem.jerseyNumber" class="font-mono text-sky-400 font-bold">
                      #{{ mem.jerseyNumber }}
                    </span>
                  </div>
                </td>
                <td class="py-2.5 px-3 font-mono text-[11px] text-slate-400">
                  {{ mem.assignedAt ? mem.assignedAt.slice(0, 10) : 'Recent' }}
                </td>
                <td class="py-2.5 px-3 text-right">
                  <NPopconfirm
                    @positive-click="handleUnassignSeat(mem)"
                    :positive-text="t('seatAllocation.unassignBtn')"
                    :negative-text="t('common.cancel')"
                  >
                    <template #trigger>
                      <NButton
                        size="tiny"
                        quaternary
                        type="error"
                        :loading="isUnassigning[mem.memberId]"
                        class="text-xs"
                      >
                        <template #icon><Trash2 class="w-3.5 h-3.5" /></template>
                        {{ t('seatAllocation.unassignBtn') }}
                      </NButton>
                    </template>
                    {{ t('seatAllocation.unassignConfirm', { name: mem.fullName }) }}
                  </NPopconfirm>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="flex items-center justify-between pt-3 border-t border-slate-800">
        <span class="text-[11px] text-slate-500 flex items-center gap-1">
          <ShieldCheck class="w-3.5 h-3.5 text-emerald-400" />
          {{ t('seatAllocation.ledgerBadge') }}
        </span>
        <NButton secondary @click="emit('update:show', false)" class="font-semibold text-xs">
          {{ t('seatAllocation.close') }}
        </NButton>
      </div>
    </div>
  </NModal>

  <!-- Capacity Exceeded Dialog -->
  <CapacityExceededDialog
    v-model:show="showCapacityDialog"
    :error-data="capacityErrorData"
  />
</template>
